import { motion } from 'framer-motion';
import { Wine, Mail, Phone, MapPin, Facebook, Instagram, Twitter } from 'lucide-react';

const Footer = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer id="contact" className="bg-gray-900 text-white pt-16 pb-8">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <Wine className="h-8 w-8" style={{color: '#D4AF37'}} />
              <span className="text-2xl font-bold">Bodega Spinoglio</span>
            </div>
            <p className="text-gray-400 mb-6">
              Cuatro generaciones de pasión vitivinícola. Del Piamonte italiano a Uruguay.
            </p>
            <div className="flex gap-4">
              <motion.a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center transition-all"
                style={{'&:hover': {background: 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}}}
                onMouseEnter={(e) => e.currentTarget.style.background = 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}
                onMouseLeave={(e) => e.currentTarget.style.background = ''}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </motion.a>
              <motion.a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center transition-all"
                onMouseEnter={(e) => e.currentTarget.style.background = 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}
                onMouseLeave={(e) => e.currentTarget.style.background = ''}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </motion.a>
              <motion.a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center transition-all"
                onMouseEnter={(e) => e.currentTarget.style.background = 'linear-gradient(135deg, #D4AF37 0%, #C9A961 100%)'}
                onMouseLeave={(e) => e.currentTarget.style.background = ''}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                aria-label="Twitter"
              >
                <Twitter className="h-5 w-5" />
              </motion.a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xl font-bold mb-4">Enlaces Rápidos</h3>
            <ul className="space-y-2">
              {[
                { name: 'Inicio', id: 'hero' },
                { name: 'Nosotros', id: 'about' },
                { name: 'Tours', id: 'tours' },
                { name: 'Restaurant', id: 'restaurant' },
                { name: 'Vinos', id: 'wines' },
                { name: 'Reservar', id: 'reservation' }
              ].map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-gray-400 transition-colors"
                    onMouseEnter={(e) => e.target.style.color = '#D4AF37'}
                    onMouseLeave={(e) => e.target.style.color = '#9CA3AF'}
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Horarios */}
          <div>
            <h3 className="text-xl font-bold mb-4">Horarios</h3>
            <ul className="space-y-2 text-gray-400">
              <li>Martes - Viernes: 10:00 - 18:00</li>
              <li>Sábado - Domingo: 10:00 - 19:00</li>
              <li>Lunes: Cerrado</li>
              <li className="pt-4" style={{color: '#D4AF37'}}>
                🍷 Tours: Con reserva previa
              </li>
              <li style={{color: '#D4AF37'}}>
                🍽️ Restaurant: Martes a Domingo
              </li>
            </ul>
          </div>

          {/* Contacto */}
          <div>
            <h3 className="text-xl font-bold mb-4">Contacto</h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 mt-1 flex-shrink-0" style={{color: '#D4AF37'}} />
                <span className="text-gray-400">
                  Cuchilla Pereyra<br />
                  Montevideo, Uruguay
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="h-5 w-5 flex-shrink-0" style={{color: '#D4AF37'}} />
                <a href="tel:+59823456789" className="text-gray-400 transition-colors"
                   onMouseEnter={(e) => e.target.style.color = '#D4AF37'}
                   onMouseLeave={(e) => e.target.style.color = '#9CA3AF'}>
                  +598 2345 6789
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-5 w-5 flex-shrink-0" style={{color: '#D4AF37'}} />
                <a href="mailto:info@bodegaspinoglio.com" className="text-gray-400 transition-colors"
                   onMouseEnter={(e) => e.target.style.color = '#D4AF37'}
                   onMouseLeave={(e) => e.target.style.color = '#9CA3AF'}>
                  info@bodegaspinoglio.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
          <p>© {new Date().getFullYear()} Bodega Spinoglio. Todos los derechos reservados.</p>
          <p className="mt-2 text-sm">
            Desarrollado con pasión por el vino y la tecnología 🍷
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
